import React, { useState, forwardRef, useRef } from 'react'
import type { Ref } from 'react'

import { TextInput } from 'components'
import { IconCalendar } from 'components/Icons'
import { useOnClickOutside } from 'helpers/hooks'
import _format from 'libs/dateFns/format'

import type { TextInputProps } from '../TextInput/TextInput'

import Calendar from '../Calendar'
import type { CalendarProps } from '../Calendar/Calendar'

export interface DatePickerProps
  extends Omit<TextInputProps, 'value' | 'onChange'>,
    Partial<Pick<CalendarProps, 'disabledDate'>> {
  value: Date | null
  onChange: (value: Date) => void
  format?: string
}

function DatePicker(
  {
    value,
    onChange,
    format = 'dd MMM yyyy',
    withIcon,
    disabledDate,
    ...others
  }: DatePickerProps,
  ref: Ref<HTMLInputElement>,
): JSX.Element {
  const base = 'date-picker-con'

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const displayText = value ? _format(value, format) : ''

  const _onChange = (value: Date) => {
    onChange(value)
    handleClose()
  }

  const handleClose = () => setIsOpen(false)

  const divRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(divRef, handleClose)

  if (isOpen || displayText !== '') {
    others.isError = false
    others.message = ''
  }
  return (
    <div
      data-testid='date-picker'
      className={`${base} ${base}__pointer`}
      ref={divRef}
    >
      <TextInput
        {...others}
        onFocus={() => setIsOpen(true)}
        value={displayText}
        withIcon={
          withIcon || (
            <IconCalendar
              className={`${base}__pointer`}
              variants='m'
              onClick={() => setIsOpen(true)}
            />
          )
        }
        readOnly
        ref={ref}
      />
      {isOpen && (
        <Calendar
          className={`${base}__calendar`}
          value={value}
          onChange={_onChange}
          disabledDate={disabledDate}
        />
      )}
    </div>
  )
}

export default forwardRef(DatePicker)
